import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { AnimatePresence, motion } from "motion/react";
import { ArrowLeft, ArrowRight, Check, CircleCheck, FileSignature, PencilLine, RotateCcw, Scale, Sparkles, Undo2 } from "lucide-react";
import type { EcInvestigationReport, ReportText } from "@ncp/api-client";
import { formatDate, formatIndexNear, formatPercentNear, useTranslation } from "@ncp/i18n";
import { Badge, Button, Card, CardContent, InlineAlert, NativeSelect, Textarea, Tooltip, cn } from "@ncp/ui";
import { REASONS_MIN, RedraftAction } from "@ncp/assistant-sdk";
import { chapterSpans, isEmptyText, mostAffectedMarket, type Evidence, type ReportSection, type SectionDecision } from "./reportSections";
import { useIsNextStep } from "../stage/nextStep";
import { markSets, useCalculationMethod } from "../figures";

interface GuidedReviewProps {
  report: EcInvestigationReport;
  sections: ReportSection[];
  decisions: Record<string, SectionDecision>;
  view: "ar" | "en";
  readOnly: boolean;
  busy: boolean;
  onDecide: (path: string, decision: SectionDecision, value?: ReportText) => void;
  onRecommendation: (code: EcInvestigationReport["recommendationCode"]) => void;
  /** The expert's own reasons for the recommendation, recorded with the approval; required before the report is finalised. */
  reasons: string;
  onReasons: (text: string) => void;
  onFinalise: () => void;
  onRedraft: () => void;
  /** Shown below the last passage and the consolidated view: the end of the report's text, where its reading is recorded (Condition 3.10). */
  textEnd?: ReactNode;
}

/**
 * The report reviewed one passage at a time. Each step shows what the agent wrote and, beside it, the figures the rules engine
 * computed for that very passage, so the officer never has to leave the text to check the number it rests on. Accepting moves
 * to the next undecided passage, so a clean report is a run of keystrokes and the reviewer stops only where they disagree.
 * The last step is the consolidated view the Department asked for: everything decided, in one place, before signing.
 */
export function GuidedReview({ report, sections, decisions, view, readOnly, busy, onDecide, onRecommendation, reasons, onReasons, onFinalise, onRedraft, textEnd }: GuidedReviewProps) {
  // A pass resumed after a reload opens at the first passage still waiting on the reviewer.
  const [step, setStep] = useState(() => {
    const first = sections.findIndex((s) => !isEmptyText(s.value) && (decisions[s.path] ?? "pending") === "pending");
    return first === -1 ? (Object.keys(decisions).length > 0 ? sections.length : 0) : first;
  });
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<ReportText>({ ar: "", en: "" });
  const cardRef = useRef<HTMLDivElement>(null);

  const summaryStep = sections.length;
  const section = sections[step];
  const decided = sections.filter((s) => decisions[s.path] && decisions[s.path] !== "pending").length;
  const overridden = sections.filter((s) => decisions[s.path] === "overridden").length;
  const outstanding = sections.filter((s) => !isEmptyText(s.value) && (decisions[s.path] ?? "pending") === "pending").length;
  const spans = useMemo(() => chapterSpans(sections), [sections]);

  const go = useCallback((next: number) => {
    setEditing(false);
    setStep(Math.max(0, Math.min(summaryStep, next)));
    cardRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
  }, [summaryStep]);

  /** After a decision, jump to the next passage still waiting on the reviewer; when none is left, go to the summary. */
  const advance = useCallback((from: number) => {
    const next = sections.findIndex((s, i) => i > from && (decisions[s.path] ?? "pending") === "pending");
    go(next === -1 ? summaryStep : next);
  }, [sections, decisions, go, summaryStep]);

  const accept = useCallback(() => {
    if (!section || readOnly || isEmptyText(section.value)) return;
    onDecide(section.path, "accepted");
    advance(step);
  }, [section, readOnly, onDecide, advance, step]);

  const startEditing = useCallback(() => {
    if (!section || readOnly) return;
    setDraft(section.value);
    setEditing(true);
  }, [section, readOnly]);

  const saveOverride = () => {
    if (!section) return;
    onDecide(section.path, "overridden", draft);
    setEditing(false);
    advance(step);
  };

  // Keyboard: the whole pass can be done without the mouse. Ignored while typing in the override boxes.
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      if (target && (target.tagName === "INPUT" || target.tagName === "TEXTAREA" || target.isContentEditable)) {
        if (e.key === "Escape") setEditing(false);
        return;
      }
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (e.key === "ArrowRight") { e.preventDefault(); go(step + 1); }
      else if (e.key === "ArrowLeft") { e.preventDefault(); go(step - 1); }
      else if (e.key.toLowerCase() === "a") { e.preventDefault(); accept(); }
      else if (e.key.toLowerCase() === "e") { e.preventDefault(); startEditing(); }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [step, go, accept, startEditing]);

  return (
    <div className="space-y-4">
      <ProgressRail sections={sections} decisions={decisions} spans={spans} step={step} summaryStep={summaryStep} decided={decided} overridden={overridden} onJump={go} />

      <AnimatePresence mode="wait" initial={false}>
        <motion.div key={step} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }} transition={{ duration: 0.18 }} ref={cardRef}>
          {step === summaryStep ? (
            <ConsolidatedStep
              report={report} sections={sections} decisions={decisions} view={view} readOnly={readOnly} busy={busy}
              outstanding={outstanding} overridden={overridden}
              onRecommendation={onRecommendation} reasons={reasons} onReasons={onReasons} onFinalise={onFinalise} onRedraft={onRedraft} onJump={go}
            />
          ) : section ? (
            <StepCard
              section={section} index={step} total={sections.length} view={view} readOnly={readOnly} draftScope={`case:${report.header.caseNumber}:report`}
              decision={decisions[section.path] ?? "pending"}
              editing={editing} draft={draft} onDraft={setDraft}
              onAccept={accept} onEdit={startEditing} onCancel={() => setEditing(false)} onSave={saveOverride}
              onPrev={() => go(step - 1)} onNext={() => go(step + 1)}
            />
          ) : null}
          {step >= sections.length - 1 ? textEnd : null}
        </motion.div>
      </AnimatePresence>
    </div>
  );
}

// ---------------------------------------------------------------- progress

/**
 * One bar across the width of the page: every passage is a segment, sized in proportion to its chapter, so the whole report is
 * legible at a glance and the rail never wraps however many passages there are. Green is accepted, gold overridden, amber blank,
 * sand still waiting; the segment the reviewer is on stands taller.
 */
function ProgressRail({ sections, decisions, spans, step, summaryStep, decided, overridden, onJump }: {
  sections: ReportSection[];
  decisions: Record<string, SectionDecision>;
  spans: Array<{ chapter: string; from: number; count: number }>;
  step: number;
  summaryStep: number;
  decided: number;
  overridden: number;
  onJump: (n: number) => void;
}) {
  const { t } = useTranslation();
  const percent = sections.length === 0 ? 0 : Math.round((decided / sections.length) * 100);

  return (
    <Card className="overflow-hidden border-aegold-200">
      <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1 px-4 pt-3">
        <p className="text-sm font-semibold text-aeblack-900">
          <span className="tabular text-lg text-aegold-800">{percent}%</span>
          <span className="ms-2">{t("backoffice:investigationReport.progress", { done: decided, total: sections.length })}</span>
          {overridden > 0 ? <span className="ms-2 font-normal text-muted-foreground">· {t("backoffice:investigationReport.overriddenCount", { count: overridden })}</span> : null}
        </p>
        <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
          <Kbd>A</Kbd> {t("actions.accept")} <Kbd>E</Kbd> {t("backoffice:investigationReport.override")} <Kbd>←</Kbd><Kbd>→</Kbd> {t("backoffice:investigationReport.move")}
        </div>
      </div>

      <div className="flex items-end gap-2 px-4 pb-4 pt-3">
        {spans.map((span) => (
          // Grow in proportion to the passages in the chapter, but never below the width its label needs to stay readable.
          <div key={`${span.chapter}-${span.from}`} className="min-w-0" style={{ flex: `${span.count} 1 ${Math.max(56, span.count * 14)}px` }}>
            <p className="mb-1.5 truncate text-[11px] font-semibold uppercase tracking-wider text-sand-500">{t(`backoffice:investigationReport.chapters.${span.chapter}`)}</p>
            <div className="flex items-end gap-0.5">
              {Array.from({ length: span.count }, (_, i) => span.from + i).map((index) => {
                const s = sections[index];
                const state = decisions[s.path] ?? "pending";
                const current = index === step;
                return (
                  <Tooltip key={s.path} content={`${index + 1}. ${s.label}`}>
                    <button
                      type="button"
                      aria-label={s.label}
                      aria-current={current}
                      onClick={() => onJump(index)}
                      className={cn(
                        "min-w-1.5 flex-1 rounded-full transition-all",
                        current ? "h-5 ring-2 ring-aegold-500 ring-offset-1" : "h-2.5 hover:h-4",
                        state === "accepted" ? "bg-success-500" : state === "overridden" ? "bg-aegold-500" : isEmptyText(s.value) ? "bg-warning-300" : "bg-sand-300",
                      )}
                    />
                  </Tooltip>
                );
              })}
            </div>
          </div>
        ))}
        <div className="shrink-0">
          <p className="mb-1.5 truncate text-[11px] font-semibold uppercase tracking-wider text-sand-500">{t("backoffice:investigationReport.chapters.summary")}</p>
          <Tooltip content={t("backoffice:investigationReport.summaryTitle")}>
            <button
              type="button"
              aria-current={step === summaryStep}
              aria-label={t("backoffice:investigationReport.summaryTitle")}
              onClick={() => onJump(summaryStep)}
              className={cn("grid h-6 w-9 place-items-center rounded-full transition-all", step === summaryStep ? "bg-aeblack-950 text-white ring-2 ring-aegold-500 ring-offset-1" : "bg-sand-200 text-sand-700 hover:bg-sand-300")}
            >
              <FileSignature className="size-3.5" />
            </button>
          </Tooltip>
        </div>
      </div>
    </Card>
  );
}

function Kbd({ children }: { children: React.ReactNode }) {
  return <kbd className="rounded border border-sand-300 bg-sand-50 px-1.5 py-0.5 font-mono text-[10px] font-medium text-sand-700">{children}</kbd>;
}

// ---------------------------------------------------------------- one passage

function StepCard({ section, index, total, view, readOnly, draftScope, decision, editing, draft, onDraft, onAccept, onEdit, onCancel, onSave, onPrev, onNext }: {
  section: ReportSection;
  index: number;
  total: number;
  view: "ar" | "en";
  readOnly: boolean;
  /** Names the officer's rewording of each passage, kept on the device until the report is saved (S 2.7). */
  draftScope: string;
  decision: SectionDecision;
  editing: boolean;
  draft: ReportText;
  onDraft: (t: ReportText) => void;
  onAccept: () => void;
  onEdit: () => void;
  onCancel: () => void;
  onSave: () => void;
  onPrev: () => void;
  onNext: () => void;
}) {
  const isNext = useIsNextStep("analysis:report");
  const { t } = useTranslation();
  const empty = isEmptyText(section.value);
  const body = view === "ar" ? section.value.ar : section.value.en || section.value.ar;

  return (
    <Card className={cn("overflow-hidden", section.conclusion ? "border-aegold-300" : "border-sand-200")}>
      <div className={cn("h-1", section.conclusion ? "bg-aegold-500" : "bg-seablue-400")} />
      <div className="grid gap-0 lg:grid-cols-[minmax(0,1.6fr)_minmax(0,1fr)]">
        <div className="min-w-0 p-5">
          <div className="mb-3 flex flex-wrap items-center gap-2">
            <span className="text-xs font-semibold uppercase tracking-wider text-sand-600">{t(`backoffice:investigationReport.chapters.${section.chapter}`)}</span>
            <span className="text-xs text-muted-foreground">· {t("backoffice:investigationReport.stepOf", { n: index + 1, total })}</span>
            {section.conclusion ? <Badge size="sm" variant="gold"><Scale /> {t("backoffice:investigationReport.loadBearing")}</Badge> : null}
            {decision === "accepted" ? <Badge size="sm" variant="success"><Check /> {t("backoffice:investigationReport.accepted")}</Badge> : null}
            {decision === "overridden" ? <Badge size="sm" variant="gold"><PencilLine /> {t("backoffice:investigationReport.overridden")}</Badge> : null}
          </div>

          <h3 className="text-lg font-semibold leading-tight text-aeblack-900">{section.label}</h3>
          <p className="mt-1 text-xs text-muted-foreground">{section.hint}</p>

          {editing ? (
            <div className="mt-4 space-y-3">
              <div>
                <div className="flex items-center justify-between gap-2">
                  <label htmlFor={`passage-ar-${section.path}`} className="text-[11px] font-semibold uppercase tracking-wider text-sand-600">العربية</label>
                  <RedraftAction format="plain" getText={() => draft.ar} onApply={(text) => onDraft({ ...draft, ar: text })} context={{ kind: `A passage of the investigation report, in Arabic (${section.chapter})`, title: section.label, guidance: section.hint, reference: draft.en || undefined }} />
                </div>
                <Textarea id={`passage-ar-${section.path}`} className="mt-1" dir="rtl" rows={7} autoFocus value={draft.ar} onChange={(e) => onDraft({ ...draft, ar: e.target.value })} draft={`${draftScope}:passage:${section.path}:ar`} />
              </div>
              <div>
                <div className="flex items-center justify-between gap-2">
                  <label htmlFor={`passage-en-${section.path}`} className="text-[11px] font-semibold uppercase tracking-wider text-sand-600">English</label>
                  <RedraftAction format="plain" getText={() => draft.en} onApply={(text) => onDraft({ ...draft, en: text })} context={{ kind: `A passage of the investigation report, in English (${section.chapter})`, title: section.label, guidance: section.hint, reference: draft.ar || undefined }} />
                </div>
                <Textarea id={`passage-en-${section.path}`} className="mt-1" dir="ltr" rows={5} value={draft.en} onChange={(e) => onDraft({ ...draft, en: e.target.value })} draft={`${draftScope}:passage:${section.path}:en`} />
              </div>
              <div className="flex justify-end gap-2">
                <Button size="sm" variant="ghost" onClick={onCancel}>{t("actions.cancel")}</Button>
                <Button size="sm" variant="gold" primary={isNext} disabled={!draft.ar.trim()} onClick={onSave}><Check /> {t("backoffice:investigationReport.applyOverride")}</Button>
              </div>
            </div>
          ) : empty ? (
            <InlineAlert tone="warning" className="mt-4" title={t("backoffice:investigationReport.emptySection")}>
              {t("backoffice:investigationReport.emptySectionHint")}
            </InlineAlert>
          ) : (
            <blockquote
              dir={view === "ar" ? "rtl" : "ltr"}
              lang={view === "ar" ? "ar" : "en"}
              className={cn("mt-4 min-h-24 whitespace-pre-wrap border-s-[3px] border-aegold-200 ps-4 text-base text-aeblack-900", view === "ar" ? "leading-[2.1]" : "leading-relaxed")}
            >
              {body}
            </blockquote>
          )}

          {!editing ? (
            <div className="mt-6 flex flex-wrap items-center gap-2 border-t border-sand-100 pt-4">
              <Button size="sm" variant="ghost" onClick={onPrev} disabled={index === 0}><ArrowLeft className="rtl:rotate-180" /> {t("backoffice:investigationReport.previous")}</Button>
              {readOnly ? null : (
                <>
                  {empty ? (
                    <Button size="lg" variant="gold" primary={isNext && decision === "pending"} onClick={onEdit} className="min-w-40"><PencilLine /> {t("backoffice:investigationReport.writePassage")}</Button>
                  ) : (
                    <>
                      <Button size="lg" variant="gold" primary={isNext && decision === "pending"} onClick={onAccept} className="min-w-40">
                        {decision === "pending" ? <><Check /> {t("actions.accept")}</> : <><Undo2 /> {t("backoffice:investigationReport.acceptAgain")}</>}
                      </Button>
                      <Button size="lg" variant="outline" onClick={onEdit}><PencilLine /> {t("backoffice:investigationReport.override")}</Button>
                    </>
                  )}
                </>
              )}
              <Button size="sm" variant="ghost" className="ms-auto" onClick={onNext}>{t("backoffice:investigationReport.skip")} <ArrowRight className="rtl:rotate-180" /></Button>
            </div>
          ) : null}
        </div>

        <EvidencePanel evidence={section.evidence} />
      </div>
    </Card>
  );
}

/** The figures behind the passage, computed by the rules engine and therefore not the agent's to change. */
function EvidencePanel({ evidence }: { evidence: Evidence[] }) {
  const { t } = useTranslation();
  return (
    <aside className="border-sand-200 bg-sand-50/70 p-5 lg:border-s">
      <p className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-sand-600">
        <Sparkles className="size-3.5 text-aegold-700" /> {t("backoffice:investigationReport.evidenceTitle")}
      </p>
      {evidence.length === 0 ? (
        <p className="mt-3 text-xs text-muted-foreground">{t("backoffice:investigationReport.noEvidence")}</p>
      ) : (
        <dl className="mt-3 space-y-2">
          {evidence.map((e, i) => (
            <div key={`${e.label}-${i}`} className="flex items-baseline justify-between gap-3 border-b border-sand-200/70 pb-1.5 last:border-0">
              <dt className="min-w-0 truncate text-xs text-muted-foreground">{e.label}</dt>
              <dd className={cn("shrink-0 text-xs tabular", e.strong ? "font-semibold text-aeblack-900" : "text-aeblack-900")}>
                {e.value}
                {e.met === undefined ? null : <span className={cn("ms-1.5", e.met ? "text-success-600" : "text-sand-400")}>{e.met ? "✔" : "✘"}</span>}
              </dd>
            </div>
          ))}
        </dl>
      )}
      <p className="mt-4 text-[11px] leading-relaxed text-sand-600">{t("backoffice:investigationReport.evidenceCaption")}</p>
    </aside>
  );
}

// ---------------------------------------------------------------- consolidated step

function ConsolidatedStep({ report, sections, decisions, view, readOnly, busy, outstanding, overridden, onRecommendation, reasons, onReasons, onFinalise, onRedraft, onJump }: {
  report: EcInvestigationReport;
  sections: ReportSection[];
  decisions: Record<string, SectionDecision>;
  view: "ar" | "en";
  readOnly: boolean;
  busy: boolean;
  outstanding: number;
  overridden: number;
  onRecommendation: (code: EcInvestigationReport["recommendationCode"]) => void;
  reasons: string;
  onReasons: (text: string) => void;
  onFinalise: () => void;
  onRedraft: () => void;
  onJump: (n: number) => void;
}) {
  const isNext = useIsNextStep("analysis:report");
  const { t } = useTranslation();
  const accepted = sections.filter((s) => decisions[s.path] === "accepted").length;
  const pending = sections.filter((s) => !isEmptyText(s.value) && (decisions[s.path] ?? "pending") === "pending");
  const blanks = sections.filter((s) => isEmptyText(s.value));
  const worst = mostAffectedMarket(report);
  // The share beside the report's own share threshold and the change beside the ΔHHI marks, in full where rounding would put either on a mark (2.9).
  const marks = markSets(useCalculationMethod().data?.bands);

  return (
    <Card className="overflow-hidden border-aegold-300">
      <div className="h-1 bg-aegold-500" />
      <CardContent className="space-y-5 p-6">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wider text-aegold-700">{t("backoffice:investigationReport.chapters.summary")}</p>
          <h3 className="mt-1 text-xl font-semibold text-aeblack-900">{t("backoffice:investigationReport.summaryTitle")}</h3>
          <p className="mt-1 text-sm text-muted-foreground">{t("backoffice:investigationReport.summaryHint")}</p>
        </div>

        <div className="grid gap-3 sm:grid-cols-3">
          <Tally tone="success" value={accepted} label={t("backoffice:investigationReport.accepted")} />
          <Tally tone="gold" value={overridden} label={t("backoffice:investigationReport.overridden")} />
          <Tally tone={outstanding > 0 ? "warning" : "muted"} value={outstanding} label={t("backoffice:investigationReport.awaitingYou")} />
        </div>

        {pending.length > 0 ? (
          <InlineAlert tone="warning" title={t("backoffice:investigationReport.stillOpen")}>
            <div className="mt-1 flex flex-wrap gap-1.5">
              {pending.slice(0, 8).map((s) => (
                <button key={s.path} type="button" onClick={() => onJump(sections.indexOf(s))} className="rounded-full border border-warning-300 bg-white px-2.5 py-0.5 text-xs hover:bg-warning-50">
                  {s.label}
                </button>
              ))}
              {pending.length > 8 ? <span className="self-center text-xs text-muted-foreground">+{pending.length - 8}</span> : null}
            </div>
          </InlineAlert>
        ) : (
          <InlineAlert tone="success" title={t("backoffice:investigationReport.allReviewed")}>{t("backoffice:investigationReport.allReviewedHint")}</InlineAlert>
        )}

        {blanks.length > 0 ? (
          <InlineAlert tone="info" title={t("backoffice:investigationReport.blanksTitle", { count: blanks.length })}>
            {t("backoffice:investigationReport.blanksHint")}
          </InlineAlert>
        ) : null}

        <div className="grid gap-4 lg:grid-cols-2">
          <div className="rounded-xl border border-sand-200 p-4">
            <p className="text-[11px] font-semibold uppercase tracking-wider text-sand-600">{t("backoffice:investigationReport.recommendation")}</p>
            <NativeSelect
              className="mt-2"
              value={report.recommendationCode}
              disabled={readOnly}
              onChange={(e) => onRecommendation(e.target.value as EcInvestigationReport["recommendationCode"])}
            >
              {(["Approve", "ApproveWithConditions", "Prohibit", "NotNotifiable"] as const).map((r) => (
                <option key={r} value={r}>{t(`backoffice:investigationReport.recommendations.${r}`)}</option>
              ))}
            </NativeSelect>
            <p dir={view === "ar" ? "rtl" : "ltr"} className="mt-3 text-sm leading-relaxed text-aeblack-900">
              {view === "ar" ? report.recommendation.ar : report.recommendation.en || report.recommendation.ar}
            </p>
            {report.proposedConditions.length > 0 ? (
              <ol className="mt-3 list-decimal space-y-1 ps-5 text-sm" dir={view === "ar" ? "rtl" : "ltr"}>
                {report.proposedConditions.map((c, i) => <li key={i}>{view === "ar" ? c.ar : c.en || c.ar}</li>)}
              </ol>
            ) : null}
            <label htmlFor="report-reasons" className="mt-4 block text-[11px] font-semibold uppercase tracking-wider text-sand-600">{t("backoffice:investigationReport.reasons")}</label>
            <Textarea
              id="report-reasons"
              className="mt-2"
              rows={3}
              value={reasons}
              readOnly={readOnly}
              onChange={(e) => onReasons(e.target.value)}
              placeholder={t("backoffice:investigationReport.reasonsPlaceholder")}
              draft={`case:${report.header.caseNumber}:report:reasons`}
            />
            {readOnly ? null : <p className="mt-1 text-[11px] text-muted-foreground">{t("backoffice:investigationReport.reasonsHint", { count: REASONS_MIN, typed: reasons.trim().length })}</p>}
          </div>

          <div className="rounded-xl border border-sand-200 p-4">
            <p className="text-[11px] font-semibold uppercase tracking-wider text-sand-600">{t("backoffice:investigationReport.keyFigures")}</p>
            <dl className="mt-2 space-y-2 text-sm">
              <Figure label={t("backoffice:investigationReport.header.caseNumber")} value={report.header.caseNumber} />
              <Figure label={t("backoffice:investigationReport.variantLabel")} value={t(`backoffice:investigationReport.variants.${report.variant}`)} />
              <Figure
                label={t("backoffice:investigationReport.thresholds.notifiable")}
                value={report.legalTest2.thresholds.some((r) => r.notifiable) ? t("misc.yes") : t("misc.no")}
              />
              {worst ? <Figure label={t("backoffice:investigationReport.evidence.worstMarket")} value={`${view === "ar" ? worst.productMarketAr : worst.productMarket} · ${formatPercentNear(worst.combinedShareAfter, view, report.legalTest2.thresholds[0]?.shareThresholdPercent ?? marks.share)} · ${t("backoffice:investigationReport.evidence.deltaHhi")} ${formatIndexNear(worst.deltaHhi, view, marks.delta)}`} /> : null}
              <Figure label={t("backoffice:investigationReport.header.reportDate")} value={reportDate(report.reportDate, view)} />
            </dl>
          </div>
        </div>

        <div className="flex flex-wrap items-center justify-end gap-2 border-t border-sand-100 pt-4">
          <Button size="sm" variant="ghost" onClick={onRedraft}><RotateCcw /> {t("backoffice:investigationReport.redraft")}</Button>
          {readOnly ? (
            <Badge variant="success"><CircleCheck /> {t("backoffice:investigationReport.finalisedBadge")}</Badge>
          ) : (
            <Tooltip content={outstanding > 0 ? t("backoffice:investigationReport.finaliseBlocked", { count: outstanding }) : reasons.trim().length < REASONS_MIN ? t("backoffice:investigationReport.reasonsBlocked") : t("backoffice:investigationReport.finaliseHint")}>
              <Button variant="gold" size="lg" primary={isNext} disabled={outstanding > 0 || reasons.trim().length < REASONS_MIN} loading={busy} onClick={onFinalise}>
                <FileSignature /> {t("backoffice:investigationReport.finalise")}
              </Button>
            </Tooltip>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function Tally({ value, label, tone }: { value: number; label: string; tone: "success" | "gold" | "warning" | "muted" }) {
  const tones = {
    success: "border-success-200 bg-success-50 text-success-800",
    gold: "border-aegold-200 bg-aegold-50 text-aegold-900",
    warning: "border-warning-200 bg-warning-50 text-warning-800",
    muted: "border-sand-200 bg-sand-50 text-sand-700",
  } as const;
  return (
    <div className={cn("rounded-xl border p-3", tones[tone])}>
      <p className="text-2xl font-semibold tabular">{value}</p>
      <p className="text-xs">{label}</p>
    </div>
  );
}

function Figure({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-baseline justify-between gap-3 border-b border-sand-100 pb-1.5 last:border-0">
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className="text-sm font-medium tabular">{value}</dd>
    </div>
  );
}

/** The report carries its dates as dd/MM/yyyy (UAE calendar date); they read as the report prints them – day, full month name, year. */
function reportDate(value: string | null | undefined, view: "ar" | "en"): string {
  const m = value?.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (!m) return value || "—";
  return formatDate(new Date(Number(m[3]), Number(m[2]) - 1, Number(m[1])), view, "d MMMM yyyy");
}
